import * as React from 'react'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'

function getColorSchemeClasses(colorScheme: string) {
  switch (colorScheme) {
    case 'blue':
      return {
        border: 'border-blue-200',
        background: 'bg-blue-50',
        text: 'text-blue-900',
        darkBorder: 'border-blue-800',
        darkBackground: 'bg-blue-400',
        darkText: 'text-blue-50',
      }
    case 'red':
      return {
        border: 'border-red-200',
        background: 'bg-red-50',
        text: 'text-red-900',
        darkBorder: 'border-red-800',
        darkBackground: 'bg-red-950',
        darkText: 'text-red-50',
      }
    case 'y':
      return {
        border: 'border-red-200',
        background: 'bg-red-50',
        text: 'text-red-900',
        darkBorder: 'border-red-800',
        darkBackground: 'bg-red-950',
        darkText: 'text-red-50',
      }
    case 'green':
      return {
        border: 'border-green-200',
        background: 'bg-green-50',
        text: 'text-green-900',
        darkBorder: 'border-green-800',
        darkBackground: 'bg-green-950',
        darkText: 'text-green-50',
      }
    default: // 'stone' or any other default value
      return {
        border: 'border-stone-200',
        background: 'bg-white',
        text: 'text-stone-950',
        darkBorder: 'border-stone-800',
        darkBackground: 'bg-stone-950',
        darkText: 'text-stone-50',
      }
  }
}

export type ColorScheme =
  | 'blue'
  | 'red'
  | 'green'
  | 'indigo'
  | 'yellow'
  | 'pink'
  | 'brand'
  | 'stone'
  | 'neutral'

const colorSchemes: ColorScheme[] = [
  'blue',
  'red',
  'green',
  'indigo',
  'yellow',
  'pink',
  'brand',
  'stone',
  'neutral',
]

export const getColorScheme = (
  colorFilter?: ColorScheme[],
  colorProp?: ColorScheme,
): ColorScheme => {
  const availableColors =
    colorFilter && colorFilter.length ? colorFilter : colorSchemes

  if (colorProp) {
    return colorProp
  }

  const randomColor =
    availableColors[Math.floor(Math.random() * availableColors.length)]

  return randomColor
}

type GridPattern = number[][]
type ColorArray = ColorScheme[]
export type ColoredPattern = (ColorScheme | 0)[][]

const predefinedPatterns: GridPattern[] = [
  [
    [1, 0, 1, 0],
    [0, 1, 0, 1],
    [1, 0, 1, 0],
    [0, 1, 0, 1],
  ],
  [
    [0, 1, 0, 1],
    [1, 0, 1, 0],
    [0, 1, 0, 1],
    [1, 0, 1, 0],
  ],
  [
    [1, 1, 0, 0],
    [1, 1, 0, 0],
    [0, 0, 1, 1],
    [0, 0, 1, 1],
  ],
  [
    [1, 0, 1, 0],
    [1, 0, 1, 0],
    [0, 1, 0, 1],
    [0, 1, 0, 1],
  ],
]

export const getRandomPattern = (): GridPattern => {
  const randomIndex = Math.floor(Math.random() * predefinedPatterns.length)
  const selectedPattern = predefinedPatterns[randomIndex]

  return selectedPattern
}

export const getColorFromScheme = (colors: ColorArray): ColorScheme => {
  if (!colors.length) throw new Error('Empty damn color array passed!')
  const randomIndex = Math.floor(Math.random() * colors.length)
  return colors[randomIndex]
}

export const generateColoredPattern = (colors: ColorArray): ColoredPattern => {
  if (colors.length < 2 || colors.length > 3) {
    throw new Error('Feed me 2 or 3 colors, nothing more, nothing less!')
  }

  const pattern = getRandomPattern()
  const coloredPattern = pattern.map((row) =>
    row.map((cell) => (cell === 1 ? getColorFromScheme(colors) : 'stone')),
  )

  return coloredPattern
}

const cardVariants = cva('rounded-xl backdrop-blur-md dark:shadow-xl', {
  variants: {
    colorScheme: {
      blue: 'border-blueish-200 bg-blueish-50 text-blueish-900 dark:border-blueish-800 dark:bg-blueish-950 dark:text-blueish-50',
      neutral:
        'sm:p-10 lg:flex-auto rounded-3xl lg:bg-gradient-to-tr from-white to-ash-100 lg:border border-black/5 dark:bg-gradient-to-tr dark:from-ebony-950 dark:to-ash-800  dark:border-white/5',
      // 'border border-neutral-800/10 bg-neutral-50 text-neutral-900 dark:border-neutral-200/10 dark:bg-neutral-800 dark:text-neutral-50',
      pink: 'border-pinkish-200 bg-pinkish-100 text-black/90 dark:border-pinkish-800 dark:bg-pinkish-500 dark:text-pinkish-50',
      indigo:
        'border-lavender-200 bg-lavender-50 text-lavender-900 dark:border-lavender-800 dark:bg-lavender-950 dark:text-lavender-50',
      red: 'border-coral-200 bg-coral-50 text-coral-900 dark:border-coral-800 dark:bg-coral-950 dark:text-coral-50',
      green:
        'border border-green-200 bg-green-50 text-black/90 dark:border-green-800 dark:bg-green-500 dark:text-green-900',
      stone:
        'sm:p-10 lg:flex-auto rounded-3xl bg-white shadow-sm lg:border border-black/10 shadow-sm dark:bg-gradient-to-tr dark:from-[#151517] dark:to-mirage-950  dark:border-white/5',
      stone2:
        'sm:p-10 lg:flex-auto rounded-3xl bg-[#F5F5F5]/50 shadow-sm lg:border border-black/10 shadow-sm dark:bg-gradient-to-tr dark:from-[#1D1D20] dark:to-[#151517]  dark:border-white/5',
      // 'border border-vanta-800/10 bg-white text-stone-950 dark:border-stone-200/10 dark:bg-vanta-800 dark:text-stone-50 ',
      section:
        'sm:p-10 lg:flex-auto rounded-3xl lg:bg-gradient-to-br from-white to-mirage-100 lg:border border-black/5 dark:bg-gradient-to-tr dark:from-ash-950 dark:to-mirage-950  dark:border-white/5',
      // 'border border-vanta-800/10 bg-white text-stone-950 dark:border-stone-200/10 dark:bg-vanta-800 dark:text-stone-50 ',
      brand:
        // 'sm:p-10 lg:flex-auto rounded-3xl border border-black/10 bg-white text-black/90 dark:border-white/10 dark:bg-ash-950/80 dark:text-white/90 bg-gradientup3',
        'sm:p-10 lg:flex-auto rounded-3xl bg-[#F2F2F2] border border-black/10 shadow-sm dark:bg-gradient-to-tr dark:from-vanta-950 dark:to-ash-900  dark:border-white/5 hover:bg-gradientup hover:transition hover:ease-in-out hover:duration-700',
      brandR:
        // 'sm:p-10 lg:flex-auto rounded-3xl border border-black/10 bg-white text-black/90 dark:border-white/10 dark:bg-ash-950/80 dark:text-white/90 bg-gradientup3',
        'sm:p-10 lg:flex-auto rounded-3xl bg-white border border-black/10 dark:bg-gradient-to-tl dark:from-vanta-950 dark:to-ash-900  dark:border-white/5 bg-gradientup',
      yellow:
        'border border-yellowish-200 bg-yellowish-300 text-black/90 dark:border-yellowish-800 dark:bg-yellowish-400/80 dark:text-black/90',
    },
  },
  defaultVariants: {
    colorScheme: 'stone',
  },
})

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  colorScheme?:
    | 'blue'
    | 'red'
    | 'green'
    | 'indigo'
    | 'yellow'
    | 'pink'
    | 'brand'
    | 'stone'
    | 'stone2'
    | 'neutral'
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, colorScheme = 'stone', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className="w-full rounded-[28px] border border-black/10 bg-[#F5F5F5]/50 px-2 py-2 shadow-sm dark:border-white/5 dark:bg-gradient-to-tr dark:from-[#1D1D20] dark:to-[#151517] sm:p-10 md:py-4  "
        {...props}
      />
    )
  },
)

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5  p-6', className)}
    {...props}
  />
))
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('font-semibold leading-5 tracking-tight ', className)}
    {...props}
  />
))
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-stone-600 dark:text-stone-500', className)}
    {...props}
  />
))
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
))
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
))
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
